import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next'

import {
  Box,
  Grid,
  Paper,
} from '@mui/material';
import {
  Switcher,
  Typography
} from '../../../../components/v2/styled';

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Payment-links (Mollie) widget settings. Minimal object under one value key
// (`mollie_links_small`):
//   { cta: boolean, show_links: boolean }
// - `cta`        : on/off for the bottom "goto payment links" button
//                  (premium-locked when the payments module is off). On by
//                  default.
// - `show_links` : on/off for the last-5 payment links list. On by default.
//                  The summary counts line always renders — only the list
//                  itself is toggled.
// No per-row visibility settings — the last-5 links list is data, not config —
// and no order setting. Two widget-level switches in one Paper block.

export type MollieLinksSmallSettingsValue = {
  cta?: boolean;
  show_links?: boolean;
};

type Props = {
  value: MollieLinksSmallSettingsValue;
  onChange: (v: MollieLinksSmallSettingsValue) => void;
  // Unused for this widget (no order setting). Kept in the shared
  // SettingsProps shape so the dialog renders it uniformly.
  order?: string[];
  onOrderChange?: (v: string[]) => void;
};

const LABEL_BASE = 'coach.personal_settings.userdetail_widgets.settings.mollie_links_small';

export const CoachSettingsWidgetMollieLinksSmall: FunctionComponent<Props> = ({
  value,
  onChange
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  // Defensive defaults so the dialog works before the first save.
  const cta = HelpersClientWidgets.getMollieLinksSmallCtaEnabled(value);
  const showLinks = HelpersClientWidgets.getMollieLinksSmallLinksEnabled(value);

  return <Grid container spacing={2}>
    <Grid item xs={12}>
      <Typography variant="caption" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.description`)}
      </Typography>
    </Grid>

    {/* Widget-level toggles. */}
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{color: 'text.secondary'}}>
        {t(`${LABEL_BASE}.options.title`)}
      </Typography>
      <Paper variant="outlined" sx={{padding: 1, marginTop: 0.5}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <HelpersClientWidgets.mollieLinksSmallCtaIcon
            sx={{fontSize: '1.2rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main}}
          />
          <Switcher
            label={t(`${LABEL_BASE}.options.cta`)}
            value={cta}
            greying
            onChange={(c) => onChange({ ...value, cta: c })}
          />
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, padding: 1, borderRadius: 0.5, marginTop: 0.5}}>
          <HelpersClientWidgets.mollieLinksSmallLinksIcon
            sx={{fontSize: '1.2rem', color: showLinks ? theme.palette.primary.main : theme.palette.secondary.main}}
          />
          <Switcher
            label={t(`${LABEL_BASE}.options.show_links`)}
            value={showLinks}
            greying
            onChange={(c) => onChange({ ...value, show_links: c })}
          />
        </Box>
      </Paper>
    </Grid>
  </Grid>;
}

// Read-only status: the two toggle icons coloured by on/off so the placed
// box summarises the config at a glance.
type StatusProps = {
  value: MollieLinksSmallSettingsValue;
  order?: string[];
};

export const CoachSettingsWidgetMollieLinksSmallStatus: FunctionComponent<StatusProps> = ({
  value
}) => {
  const theme = useTheme();
  const {t} = useTranslation(['translations']);

  const cta = HelpersClientWidgets.getMollieLinksSmallCtaEnabled(value);
  const showLinks = HelpersClientWidgets.getMollieLinksSmallLinksEnabled(value);

  return <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 1}}>
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5}}>
      <Box title={t(`${LABEL_BASE}.options.show_links`)}>
        <HelpersClientWidgets.mollieLinksSmallLinksIcon
          sx={{ fontSize: '1.1rem', color: showLinks ? theme.palette.primary.main : theme.palette.secondary.main }}
        />
      </Box>
      <Box title={t(`${LABEL_BASE}.options.cta`)}>
        <HelpersClientWidgets.mollieLinksSmallCtaIcon
          sx={{ fontSize: '1.1rem', color: cta ? theme.palette.primary.main : theme.palette.secondary.main }}
        />
      </Box>
    </Box>
  </Box>;
}
